import React, { useEffect, useRef, useState } from "react";
import { apiUrl } from "../../commons/inFormTypes";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

const googleErrors = {
    GOOGLE_NOT_CONFIGURED: ["ورود با گوگل روی سرور تنظیم نشده است.", "Google sign-in is not configured on the server."],
    GOOGLE_CREDENTIAL_INVALID: ["اعتبار پاسخ گوگل تأیید نشد. دوباره وارد شوید؛ در صورت تکرار، تنظیمات ورود گوگل باید بررسی شود.", "Google's response could not be verified. Sign in again; if this persists, Google sign-in configuration needs checking."],
    GOOGLE_CREDENTIAL_MISSING: ["پاسخ ورود از گوگل دریافت نشد. دوباره تلاش کنید.", "No Google credential was received. Please retry."],
    GOOGLE_EMAIL_UNVERIFIED: ["ابتدا آدرس ایمیل حساب گوگل خود را تأیید کنید.", "Please verify your Google account email first."],
    GOOGLE_VERIFICATION_UNAVAILABLE: ["سرور نتوانست به سرویس تأیید گوگل متصل شود. کمی بعد تلاش کنید.", "The server could not reach Google's verification service. Please try later."],
    GOOGLE_SESSION_FAILED: ["ساخت نشست ورود روی سرور انجام نشد. با پشتیبانی تماس بگیرید.", "The server could not create a sign-in session. Please contact support."],
    DATABASE_UPDATE_FAILED: ["ذخیره حساب گوگل در پایگاه داده انجام نشد. با پشتیبانی تماس بگیرید.", "Your Google account could not be saved. Please contact support."],
};

const GoogleLoginButton = ({ text = "signin_with", redirectTo = "/user/dashboard" }) => {
    const btnRef = useRef(null);
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const { isEnglish } = useLanguage();

    useEffect(() => {
        const controller = new AbortController();
        let timer;
        setError(null);
        btnRef.current?.replaceChildren();
        const handleCredential = async (response) => {
            try {
                const res = await fetch(apiUrl + "auth/GoogleSignIn", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ credential: response.credential }),
                });

                if (res.ok) {
                    const data = await res.json();
                    if (!data?.token || !data?.role) throw new Error("Invalid sign-in session");
                    const cookies = new Cookies();
                    cookies.set("token", data.token, { path: "/" });
                    cookies.set("role", data.role, { path: "/" });
                    if (data.role === "Admin") {
                        navigate(redirectTo || "/mainadmin/users");
                    } else {
                        navigate(redirectTo || "/user/dashboard");
                    }
                } else {
                    const problem = await res.json().catch(() => ({}));
                    setError({ code: problem.code || problem.errorCode, traceId: problem.traceId });
                }
            } catch {
                setError({});
            }
        };

        const initialize = async () => {
            try {
                const response = await fetch(apiUrl + "auth/GoogleConfig", { signal: controller.signal, cache: "no-store" });
                if (!response.ok) throw new Error("Google configuration unavailable");
                const { clientId } = await response.json();
                if (typeof clientId !== "string" || !clientId.trim()) throw new Error("Missing Google client ID");
                if (controller.signal.aborted) return;

                let attempts = 0;
                timer = setInterval(() => {
                    attempts += 1;
                    if (window.google?.accounts?.id && btnRef.current) {
                        clearInterval(timer);
                        window.google.accounts.id.initialize({
                            client_id: clientId.trim(),
                            callback: handleCredential,
                            auto_select: false,
                            cancel_on_tap_outside: true,
                        });
                        window.google.accounts.id.renderButton(btnRef.current, {
                            type: "standard",
                            theme: "outline",
                            size: "large",
                            text,
                            shape: "rectangular",
                            width: Math.min(btnRef.current.offsetWidth || 320, 400),
                        });
                        return;
                    }
                    if (attempts >= 20) {
                        clearInterval(timer);
                        setError({});
                    }
                }, 250);

            } catch {
                if (!controller.signal.aborted) setError({});
            }
        };
        initialize();

        return () => {
            controller.abort();
            clearInterval(timer);
        };
    }, [isEnglish, navigate, redirectTo, text]);

    return (
        <div className="flex flex-col items-center w-full">
            {error && <p role="alert">{googleErrors[error.code]?.[isEnglish ? 1 : 0] || (isEnglish ? "Google sign-in is unavailable. Please try again later." : "ورود با گوگل انجام نشد. لطفاً دوباره تلاش کنید.")}{error.traceId && <small className="block" dir="ltr">{isEnglish ? "Reference" : "کد پیگیری"}: {error.traceId}</small>}</p>}
            <div ref={btnRef} className="flex w-full justify-center"></div>
        </div>
    );
};

export default GoogleLoginButton;
